import React from "react";
import { Box, FormControl, InputLabel, MenuItem, Select, TextField, Tooltip } from "@mui/material";
import { usesWerke, useTransportV2 } from "../context/TransportV2Context";
import { useLang } from "../i18n";
import { validPlantNaming } from "../utils/plantNaming";

export default function PlantNamingControl() {
  const { state, dispatch } = useTransportV2();
  const { t } = useLang();
  if (!usesWerke(state)) return null;
  const mode = state.plantNaming?.mode || "auto";
  const blocked = (state.selectedPackages || []).length > 1;
  return <Tooltip title={t(mode === "existing" ? "naming.hint" : mode === "generic" ? "naming.genericHint" : "naming.automatic")} arrow>
    <FormControl size="small" sx={{ minWidth: 210, maxWidth: "100%" }} disabled={!!state.batch || !!state.batchRequest}>
      <InputLabel id="plant-naming-label" sx={{ color: "var(--iio-text-3)", fontFamily: "inherit" }}>{t("naming.label")}</InputLabel>
      <Select id="plant-naming-mode" labelId="plant-naming-label" label={t("naming.label")} value={mode}
        MenuProps={{ TransitionProps: { onExited: () => document.getElementById("source-plant-field")?.focus() } }}
        onChange={event => dispatch({ type: "SET_PLANT_NAMING", payload: event.target.value === "existing"
          ? { mode: "existing", sourcePlant: "" } : { mode: event.target.value } })}
        sx={{ height: 44, borderRadius: "10px", fontFamily: "inherit", fontSize: 14, fontWeight: 600,
          color: "var(--iio-text-1)", backgroundColor: "var(--iio-bg-1)",
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--iio-border)" },
          "&:hover .MuiOutlinedInput-notchedOutline, &.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "var(--iio-signal)" },
          "& .MuiSelect-icon": { color: "var(--iio-text-3)" },
        }}>
        <MenuItem value="auto">{t("naming.auto")}</MenuItem>
        <MenuItem value="generic">{t("naming.generic")}</MenuItem>
        <MenuItem value="existing" disabled={blocked} title={blocked ? t("naming.packageLimit") : undefined}>{t("naming.existing")}</MenuItem>
      </Select>
    </FormControl>
  </Tooltip>;
}

export function PlantNamingField() {
  const { state, dispatch } = useTransportV2();
  const { t } = useLang();
  if (!usesWerke(state) || state.plantNaming?.mode !== "existing") return null;
  const invalid = !validPlantNaming(state.plantNaming);
  return <Box sx={{ mb: 3, p: { xs: 2, sm: 3 }, border: "1px solid var(--iio-border)", borderRadius: "10px", backgroundColor: "var(--iio-bg-1)" }}>
    <p className="tiny muted" style={{ margin: "0 0 16px" }}>{t("naming.hint")}</p>
    <TextField id="source-plant-field" label={t("naming.sourcePlant")} variant="outlined" fullWidth
      autoFocus value={state.plantNaming.sourcePlant} error={invalid} disabled={!!state.batch || !!state.batchRequest}
      onChange={event => dispatch({ type: "SET_PLANT_NAMING", payload: { mode: "existing", sourcePlant: event.target.value } })}
      helperText={t(invalid ? "naming.invalid" : "naming.singlePackage")}
      inputProps={{ maxLength: 32, autoComplete: "off", spellCheck: false }}
      sx={{ maxWidth: 640, "& .MuiInputBase-root, & .MuiFormLabel-root, & .MuiFormHelperText-root": { fontFamily: "inherit" },
        "& .MuiInputBase-root": { color: "var(--iio-text-1)", backgroundColor: "var(--iio-bg-2)", borderRadius: "8px" },
        "&& .MuiOutlinedInput-root": { height: "52px", fontSize: "16px", lineHeight: "24px" },
        "&& .MuiOutlinedInput-root input.MuiOutlinedInput-input": {
          boxSizing: "border-box", height: "52px", padding: "14px", margin: 0,
          fontFamily: "inherit", fontSize: "16px", lineHeight: "24px", color: "inherit",
          background: "transparent", border: 0, boxShadow: "none",
        },
        "&& .MuiInputLabel-outlined": {
          top: 0, left: 0, margin: 0, padding: 0, fontSize: "16px", lineHeight: "24px",
          transform: "translate(14px, 14px) scale(1)",
          "&.MuiInputLabel-shrink": { transform: "translate(14px, -9px) scale(0.75)" },
        },
        "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--iio-border)" },
        "& .MuiFormLabel-root, & .MuiFormHelperText-root": { color: "var(--iio-text-3)" },
        "& .Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "var(--iio-signal)" },
        "& .Mui-focused.MuiFormLabel-root": { color: "var(--iio-text-1)" },
        "& .Mui-error": { color: "var(--iio-critical)" },
        "&& input.MuiInputBase-input:focus": { boxShadow: "none !important", outline: "none" },
      }} />
  </Box>;
}
